import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DocumentService } from "../../../../../../api";
import { CircularLoader, CustomTooltip, icon, IconComponent, UserAvatar } from "../../../../../../common/components";
import { useApi } from "../../../../../../common/hooks";
import {
	environmentSwitch,
	isSegFeatureEnabled,
	SegFlags,
	translate,
	translateDate,
	translateDateAndTime,
} from "../../../../../../common/providers";
import styles from "./FileInformation.module.css";
import { FILE_TYPES } from "../../../../../../common/constants/file-types";
import { setRefreshSelectedDocument } from "../../../../slice/document-center-slice";

const conditionalColumns = environmentSwitch({
	main: [
		{ label: "document-center.side-panel.information.file.pages", valueRenderer: ({ totalPages }) => totalPages },
		{
			label: "document-center.side-panel.information.file.requirements",
			valueRenderer: ({ totalReqs }) => totalReqs,
		},
		{ label: "document-center.side-panel.information.file.version", valueRenderer: ({ version }) => version },
		{
			label: "document-center.side-panel.information.file.previous-version",
			valueRenderer: ({ previousVersionName }) => previousVersionName || "-",
		},
	],
	veolia: [
		{
			label: "document-center.table.column.number",
			valueRenderer: ({ documentAttributeDTOs }) =>
				documentAttributeDTOs?.find((attribute) => attribute.attributeKey === "Numéro")?.attributeValue || "-",
		},
		{
			label: "document-center.table.column.effective-date",
			valueRenderer: ({ documentAttributeDTOs }) =>
				translateDate(
					documentAttributeDTOs?.find((attribute) => attribute.attributeKey === "Date d'effet du texte")
						?.attributeValue
				),
		},
		{
			label: "document-center.table.column.nature",
			valueRenderer: ({ documentAttributeDTOs }) =>
				documentAttributeDTOs?.find((attribute) => attribute.attributeKey === "Nature du texte contractuel")
					?.attributeValue || "-",
		},
		{
			label: "document-center.table.column.type-of-text",
			valueRenderer: ({ documentAttributeDTOs }) =>
				documentAttributeDTOs?.find((attribute) => attribute.attributeKey === "Type de texte")
					?.attributeValue || "-",
		},
		{ label: "document-center.side-panel.information.file.pages", valueRenderer: ({ totalPages }) => totalPages },
	],
});

const PREVIEW_HEIGHT = 300;

export default function FileInformation({
	docId,
	selectedDocInformation,
	onSetDirectoryId,
	onSetSelectedDocInformation,
	isOngoing = false,
	unsupported = false,
}) {
	const {
		call: getInformation,
		cancel: cancelGetInformation,
		isLoading: isLoadingInformation,
	} = useApi(DocumentService.getInformation);
	const { call: getPage, cancel: cancelGetPage, isLoading: isLoadingPage } = useApi(DocumentService.getPage);
	const refreshSelectedDocument = useSelector(({ documentCenter }) => documentCenter.refreshSelectedDocument);
	const companyId = useSelector(({ context }) => context.company?.id);
	const projectId = useSelector(({ context }) => context.project?.id);
	const [image, setImage] = useState(null);
	const [information, setInformation] = useState(null);
	const dispatch = useDispatch();
	const rows = useMemo(
		() => [
			{ label: "document-center.side-panel.information.file.document", valueRenderer: ({ name }) => name },
			{
				label: "document-center.side-panel.information.file.filename",
				valueRenderer: ({ filename }) => filename,
			},
			{
				label: "document-center.side-panel.information.file.type",
				valueRenderer: ({ fileType, name }) => {
					if (fileType !== FILE_TYPES.UNSUPPORTED) {
						return fileType;
					}
					return name?.split(".").pop() || "";
				},
			},
			{
				label: "document-center.side-panel.information.file.location",
				valueRenderer: ({ location }) => location,
			},
			...conditionalColumns,
			{
				label: "document-center.side-panel.information.file.uploaded",
				valueRenderer: ({ uploaded }) => translateDateAndTime(uploaded),
			},
			{
				label: "document-center.side-panel.information.file.uploaded-by",
				valueRenderer: ({ username, userId }, { companyId: ctxCompanyId, projectId: ctxProjectId }) => (
					<span className={styles.row__uploaderContainer}>
						{userId && (ctxCompanyId || ctxProjectId) && (
							<UserAvatar
								className={styles.row__uploaderAvatar}
								companyId={ctxCompanyId}
								displayName={username}
								projectId={ctxProjectId}
								size="xs"
								userId={userId}
							/>
						)}
						<span>{username}</span>
					</span>
				),
				isCustomRenderer: true,
			},
			{
				label: "document-center.side-panel.information.file.resume",
				icon: icon.faWandMagicSparkles,
				disabled: !isSegFeatureEnabled(SegFlags.AI_DOCUMENT_SUMMARY),
				valueRenderer: ({ documentAttributeDTOs }) =>
					documentAttributeDTOs?.find((attribute) => attribute.attributeKey === "AI_SUMMARY_LONG")
						?.attributeValue || "-",
			},
		],
		[]
	);
	const switchedInformation = useMemo(
		() => environmentSwitch({ main: information, veolia: selectedDocInformation }),
		[information, selectedDocInformation]
	);
	const handleMainInformationResponse = useCallback(
		(data) => {
			if (typeof onSetDirectoryId === "function") {
				onSetDirectoryId(data.directoryId);
			}
			setInformation(data);
		},
		[onSetDirectoryId]
	);
	useEffect(() => {
		if (!docId || !refreshSelectedDocument) {
			return undefined;
		}
		getInformation({ docId })
			.then((data) =>
				environmentSwitch({
					main: handleMainInformationResponse(data),
					veolia: onSetSelectedDocInformation(data),
				})
			)
			.catch(console.error)
			.finally(() => dispatch(setRefreshSelectedDocument(false)));
		return () => {
			cancelGetInformation();
		};
	}, [
		docId,
		refreshSelectedDocument,
		onSetSelectedDocInformation,
		handleMainInformationResponse,
		dispatch,
		getInformation,
		cancelGetInformation,
	]);

	useEffect(() => {
		if (!docId || unsupported || isOngoing || !switchedInformation?.totalPages) {
			return undefined;
		}
		getPage({ docId }, { page: 1 })
			.then((data) => {
				if (!data) {
					return;
				}
				const im = new Image();
				const blobImage = URL.createObjectURL(data);
				im.src = blobImage;
				setImage(im.src);
			})
			.catch((err) => {
				console.error(err);
			});
		return () => {
			cancelGetPage();
		};
	}, [docId, isOngoing, unsupported, getPage, cancelGetPage, switchedInformation]);
	const isLoading = useMemo(() => isLoadingInformation || isLoadingPage, [isLoadingInformation, isLoadingPage]);
	return (
		(!isLoading && (
			<div className={styles.file__container}>
				<div className={styles.file__information}>
					{switchedInformation &&
						rows
							.filter((row) => !row.disabled)
							.map((row) => {
								if (
									unsupported &&
									(row.label.includes("pages") || row.label.includes("requirements"))
								) {
									return null;
								}
								return (
									<div key={row.label} className={styles.file__row}>
										<CustomTooltip showWhenOverflow title={translate(row.label)}>
											<span className={styles.row__label}>
												{translate(row.label)}{" "}
												{row.icon && (
													<span className={styles.row__icon}>
														<IconComponent
															color="var(--blue-500)"
															icon={row.icon}
															size="2xs"
														/>
													</span>
												)}
											</span>
										</CustomTooltip>
										<div className={styles.row_value_column}>
											<span className={styles.row__value}>
												{row.isCustomRenderer
													? row.valueRenderer(switchedInformation, { companyId, projectId })
													: row.valueRenderer(switchedInformation)}
											</span>
										</div>
									</div>
								);
							})}
				</div>
				<div className={styles.file__iconContainer}>
					{image && <img alt={`img_${1}`} height={PREVIEW_HEIGHT} src={image} />}
				</div>
			</div>
		)) || <CircularLoader />
	);
}
